import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Loader2, Send } from "lucide-react";
import { cn } from "@/lib/utils";

interface SendButtonProps {
  /** Click handler for sending. */
  onClick: () => void;
  /** Whether the button is disabled. */
  disabled?: boolean;
  /** Whether a send is in progress — shows a spinner. */
  loading?: boolean;
  /** "icon" renders a Send icon, "text" renders the word "Send". Defaults to "icon". */
  variant?: "icon" | "text";
  /** Icon size in pixels. Defaults to undefined (uses the Button's default SVG size). */
  iconSize?: number;
  /** Additional class names forwarded to the underlying Button. */
  className?: string;
}

const SHORTCUT_LABEL = navigator.platform.includes("Mac") ? "⌘+↵" : "Ctrl+↵";

/**
 * Send button with a tooltip showing the keyboard shortcut (Cmd/Ctrl+Enter).
 * @param props - SendButtonProps
 * @returns A Button wrapped in a Tooltip
 */
export function SendButton({
  onClick,
  disabled,
  loading,
  variant = "icon",
  iconSize,
  className,
}: SendButtonProps) {
  const iconStyle =
    iconSize !== undefined ? { width: iconSize, height: iconSize } : undefined;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={onClick}
            disabled={disabled}
            size={variant === "icon" ? "icon" : "default"}
            className={cn(className)}
          >
            {loading ? (
              <Loader2 className="animate-spin" style={iconStyle} />
            ) : variant === "icon" ? (
              <Send style={iconStyle} />
            ) : (
              "Send"
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Send {SHORTCUT_LABEL}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
